import _ from 'lodash';

import { createAttributeRegExp } from './createAttributeRegExp';

const INT_REG = /^\d+$/;

export function valueSelector(item: any, selector: string): unknown {
    return compileValueSelector(selector)(item);
}

export function compileValueSelector(selector: string): (item: any) => any {
    const operations = selector.split('|').map(x => x.trim());
    const chain: Array<(...args: any[]) => any> = [];

    for (const expression of operations) {
        if (expression[0] === '.') {
            const parts = _.toPath(expression).slice(1);
            chain.push(item => selectMapPattern(item, parts));
            continue;
        }

        if (!HELPER_REG.test(expression)) {
            throw new Error(`Invalid expression syntax "${expression}" in  "${selector}"`);
        }

        const { name, args } = parseFunctionCall(expression);
        const config = helpers[name];
        if (!config) throw new Error(`Invalid helper "${expression}" in "${selector}"`);

        const [fn, argTypes = []] = config;
        if (args.length > argTypes.length) {
            throw new Error(`Max ${argTypes.length} allowed in "${expression}"`);
        }

        const parsedArgs = args.map((a, i) => {
            a = a[0] === '"' ? a.slice(1, -1) : a;
            switch (argTypes[i]) {
                case 'float':
                    return parseFloat(a);
                case 'int':
                    return parseInt(a, 10);
                case 'string':
                    return a;
                case 'any':
                    return parseAny(a);
            }
        });

        chain.push(item => fn(item, ...parsedArgs));
    }

    return function compiledValueSelector(item: any): any {
        let result = item;
        for (let i = 0; i < chain.length; ++i) result = chain[i](result);
        return result;
    };
}

const cleanArray = (a: any) => (Array.isArray(a) ? a.filter(exists) : a);

export const selectMapPattern = (item: Record<string, any>, parts: string[]): any => {
    if (!parts.length) return item;
    if (item == null || typeof item !== 'object') return;

    const key = parts[0];
    const done: (a: any) => any = cleanArray;

    if (Array.isArray(item)) {
        if (INT_REG.test(key)) return done(selectMapPattern(item[key as any], parts.slice(1)));
        return done(item.map(child => selectMapPattern(child, parts)).flat());
    }

    const nextParts = parts.slice(1);

    if (key === '*') return done(_.map(item, child => selectMapPattern(child, nextParts)));

    return done(selectMapPattern(item[key], nextParts));
};

const HELPER_REG = /^([a-z]+)\(([^)]*)\)$/i;
const NUMBER_REG = /^\d+(\.\d+)?$/;

function parseAny(a: any) {
    if (a === 'null') return null;
    if (a === 'false') return null;
    if (a === 'true') return null;
    return NUMBER_REG.test(a) ? parseFloat(a) : a;
}

function addArraySupport<T extends (...args: any) => any>(fn: T): T {
    return ((item: any, ...args: any[]) => {
        if (Array.isArray(item)) return item.map(child => fn(child, ...args));
        return fn(item, ...args);
    }) as any;
}

function exists(x: any) {
    return x != null && x !== '';
}

type HelperMap = Record<
    string,
    [run: (input: any, ...args: any[]) => any, args?: Array<'string' | 'int' | 'float' | 'any'>]
>;

const helpers: HelperMap = {
    filter: [
        (item, path, op, operand) => {
            if (!Array.isArray(item)) return null;

            if (path && path[0] !== '.')
                throw new Error('invalid filter(), first argument must start with "."');
            const pathArr = path ? (path === '.' ? [] : _.toPath(path).slice(1)) : [];

            if (!op) {
                if (pathArr.length) return item.filter(child => exists(_.get(child, pathArr)));
                return item.filter(exists);
            }

            if (op !== '=' && operand == null) {
                throw new Error('invalid filter(), needs 3rd argument when using a comparisson');
            }

            operand = parseAny(operand);
            const operandPath =
                typeof operand === 'string' && operand[0] === '.' && operand !== '.'
                    ? _.toPath(operand).slice(1)
                    : null;

            return item.filter(child => {
                const value = operandPath ? _.get(child, operandPath) : operand;
                if (pathArr.length) child = _.get(child, pathArr);
                if (op === '=') return value == null ? child == value : child === value;
                if (typeof child !== 'number') return false;
                switch (op) {
                    case '<':
                        return child < value;
                    case '<=':
                        return child <= value;
                    case '>':
                        return child > value;
                    case '>=':
                        return child >= value;
                    default:
                        throw new Error(`invalid filter(), unknown operation "${op}"`);
                }
            });
        },
        ['string', 'string', 'any'],
    ],
    attr: [
        (item, pattern, matchKey = 'type') => {
            if (!pattern) throw new Error('invalid attr(), pattern is required');
            if (!Array.isArray(item)) {
                if (Array.isArray(item?.attributes)) item = item.attributes;
                else return null;
            }
            const reg = createAttributeRegExp(pattern);
            return item.filter((a: any) => a[matchKey] && reg.test(a[matchKey]));
        },
        ['string', 'string'],
    ],
    flatten: [_.flatten],
    flattenDeep: [_.flattenDeep],
    join: [_.join, ['string']],
    map: [_.map, ['string']],
    reverse: [_.reverse, []],
    slice: [_.slice, ['int', 'int']],
    size: [_.size],
    sort: [a => (Array.isArray(a) ? Array.from(a).sort() : a)],
    uniq: [_.uniq],
    uniqBy: [_.uniqBy, ['string']],
    unique: [_.uniq],

    ..._.mapValues(
        {
            camelCase: [_.camelCase, []],
            kebabCase: [_.kebabCase, []],
            lowerCase: [_.lowerCase, []],
            lowerFirst: [_.lowerFirst, []],
            snakeCase: [_.snakeCase, []],
            trim: [_.trim, ['int']],
            upperCase: [_.upperCase, []],
            truncate: [
                (s, length = 20, omission = '…') => _.truncate(s, { length, omission }),
                ['int', 'string'],
            ],
            words: [_.words, ['string']],
        } satisfies HelperMap,
        a => [addArraySupport(a[0]), ...a.slice(1)] as any,
    ),
};

/**
 * Fully generated by ChatGPT with no changes
 * I did slap some tests on it to make sure it works ... it works
 * I, for one, welcome our new generative AI overlords
 */
export function parseFunctionCall(expression: string): { name: string; args: string[] } {
    // Validate input
    if (expression.trim() === '') {
        throw new Error('Invalid expression');
    }

    // Extract the function name and argument list from the expression
    const match = expression.match(/^(\w+)\((.*)\)$/);
    if (!match) {
        throw new Error('Invalid expression');
    }
    const functionName = match[1];
    const argList = match[2];

    // Split the argument list into individual arguments
    const args = [];
    let argStart = 0;
    let inString = false;
    let wasInString = false;
    let quoteChar = '';
    for (let i = 0; i < argList.length; i++) {
        const c = argList[i];
        if (c === ',' && !inString) {
            let arg = argList.slice(argStart, i).trim();
            if (arg === '') {
                throw new Error('Empty argument in argument list');
            }
            if (wasInString && arg[0] === "'") arg = '"' + arg.slice(1, -1) + '"';
            args.push(arg);
            wasInString = false;
            argStart = i + 1;
        } else if (c === "'" || c === '"') {
            if (!inString) {
                inString = true;
                wasInString = true;
                quoteChar = c;
            } else if (c === quoteChar) {
                inString = false;
                wasInString = true;
                quoteChar = '';
            }
        }
    }
    let lastArg = argList.slice(argStart).trim();
    if (lastArg !== '') {
        if (wasInString && lastArg[0] === "'") lastArg = '"' + lastArg.slice(1, -1) + '"';
        args.push(lastArg);
    } else if (argList.endsWith(',')) {
        throw new Error('Dangling comma in argument list');
    }

    // Return the function name followed by the argument array
    return { name: functionName, args };
}
